import './FoodDetailModal.css';

import FoodImage from './FoodImage';
import NutriScoreDisplay from './NutriScoreDisplay';
import { getRandomCalories } from '../utils/utils';
import { useState } from 'react';

const FoodDetailModal = ({ item, isVisible, onClose, onUpgradeClick, userPlan }) => {
  const [showCaloriesUpgradeTooltip, setShowCaloriesUpgradeTooltip] = useState(false);
  const [showNutrientTooltip, setShowNutrientTooltip] = useState(null);

  if (!isVisible || !item) return null;

  const menuName = typeof item === 'string' ? item : item.menuResponse;
  const imageUrls = typeof item === 'object' ? item.imageUrls : null;
  const nutritionalScore = typeof item === 'object' ? item.nutritional_score : null;
  const approximateCalories = typeof item === 'object' ? item.approximate_calories : null;
  const nutritionalInformation = typeof item === 'object' ? item.nutritional_summary : null;
  const description = typeof item === 'object' ? item.description : null;
  const price = typeof item === 'object' ? item.price : null;

  // Check if user is paying (same logic as FilterControls)
  const isPayingUser = userPlan && userPlan !== 'free';

  // Get dietary badges with just icons
  const getDietaryBadges = item => {
    if (typeof item !== 'object') return [];

    const badges = [];
    if (item.is_vegetarian === true) badges.push({ icon: '🥬', label: 'Vegetarian' });
    if (item.is_vegan === true) badges.push({ icon: '🌱', label: 'Vegan' });
    return badges;
  };

  // Format allergens breakdown for display
  const formatAllergensBreakdown = item => {
    // Allergen key mapping with emojis - matching FilterControls order
    const keyMap = {
      milk: { name: 'Milk', emoji: '🥛' },
      cereals_containing_gluten: { name: 'Gluten', emoji: '🌾' },
      eggs: { name: 'Eggs', emoji: '🥚' },
      peanuts: { name: 'Peanuts', emoji: '🥜' },
      nuts: { name: 'Tree Nuts', emoji: '🌰' },
      soybeans: { name: 'Soybeans', emoji: '🌱' },
      fish: { name: 'Fish', emoji: '🐟' },
      crustaceans: { name: 'Crustaceans', emoji: '🦐' },
      molluscs: { name: 'Molluscs', emoji: '🦪' },
      sesame_seeds: { name: 'Sesame Seeds', emoji: '⚫' },
      celery: { name: 'Celery', emoji: '🌿' },
      mustard: { name: 'Mustard', emoji: '🌭' },
      lupin: { name: 'Lupin', emoji: '🌾' },
      sulphur_dioxide_and_sulphites: { name: 'Sulphites', emoji: '🍷' },
    };

    const statusMap = {
      high: { icon: '⚠️', color: '#ff4d4f', displayText: 'High allergen risk' },
    };

    const formatValue = (key, value) => {
      // For free users, always show blurred placeholders regardless of actual data
      if (!isPayingUser) {
        const allergenInfo = keyMap[key];

        return {
          key: `${allergenInfo.emoji} ${allergenInfo.name}`,
          value: 'blurred_placeholder',
          icon: '🔒', // Lock icon for free users
          color: '#999',
          displayValue: 'Upgrade to view',
          isBlurred: true,
        };
      }

      // For paying users, only show allergens with "high" risk
      if (value === 'high') {
        const status = statusMap[value];
        const allergenInfo = keyMap[key];
        return {
          key: `${allergenInfo.emoji} ${allergenInfo.name}`,
          value: value,
          icon: status.icon,
          color: status.color,
          displayValue: status.displayText,
          isBlurred: false,
        };
      }

      // If paying user but allergen is not "high" risk, don't show it
      return null;
    };

    // Read allergen values in the same order as FilterControls
    const allergenKeys = [
      // Most Common Allergens
      'milk',
      'cereals_containing_gluten',
      'eggs',
      'peanuts',
      'nuts',
      'soybeans',
      'fish',
      'crustaceans',
      'molluscs',
      // Moderately Common or Region-Specific
      'sesame_seeds',
      'celery',
      'mustard',
      // Less Common Allergens
      'lupin',
      'sulphur_dioxide_and_sulphites',
    ];

    // For free users, show some blurred placeholders
    // For paying users, only show allergens that have "high" risk
    if (!isPayingUser) {
      // Show first 4 allergens as blurred placeholders for free users
      return allergenKeys
        .slice(0, 4)
        .map(key => formatValue(key, 'placeholder'))
        .filter(allergen => allergen !== null);
    } else {
      // For paying users, only show allergens with "high" risk
      return allergenKeys.map(key => formatValue(key, item?.[key])).filter(allergen => allergen !== null);
    }
  };

  const badges = getDietaryBadges(item);
  const allergenBreakdown = formatAllergensBreakdown(item);

  // Handle overlay click
  const handleOverlayClick = e => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Handle upgrade link click
  const handleUpgradeClick = e => {
    e.stopPropagation();
    onClose(); // Close the modal first
    onUpgradeClick();
  };

  // Handle calories click
  const handleCaloriesClick = e => {
    e.stopPropagation();
    onClose(); // Close the modal first
    onUpgradeClick();
  };

  return (
    <div className="food-modal-overlay" onClick={handleOverlayClick}>
      <div className="food-modal-container" onClick={e => e.stopPropagation()}>
        <button className="food-modal-close" onClick={onClose} aria-label="Close modal">
          ✕
        </button>

        <div className="food-modal-content">
          <div className="food-modal-image-section">
            {imageUrls && imageUrls.length > 0 ? (
              <FoodImage imageUrls={imageUrls} altText={menuName} />
            ) : (
              <div className="food-image-placeholder-modal">
                <span className="placeholder-icon-modal">🍽️</span>
                <small>No image available</small>
              </div>
            )}
          </div>

          <div className="food-modal-content-section">
            <div className="food-title-row">
              <h2 className="food-title-airbnb">{menuName}</h2>
              {price && <span className="food-price-modal">{price}</span>}
            </div>

            <p className="food-location-airbnb">Menu item details</p>

            <div className="food-stats-row">
              <div className="nutri-score-section-modal">
                <NutriScoreDisplay score={nutritionalScore} size="normal" />
              </div>

              <div className="stats-divider">•</div>

              <div className="calories-airbnb">
                {!approximateCalories ? (
                  <div
                    className="calories-modal-clickable"
                    onClick={handleCaloriesClick}
                    onMouseEnter={() => setShowCaloriesUpgradeTooltip(true)}
                    onMouseLeave={() => setShowCaloriesUpgradeTooltip(false)}
                  >
                    <span className="calories-value">
                      🔥 <span className="blurred">{getRandomCalories()}</span> calories
                    </span>
                    {showCaloriesUpgradeTooltip && (
                      <div className="upgrade-tooltip-modal">🔒 Upgrade to unlock calories</div>
                    )}
                  </div>
                ) : (
                  <span className="calories-value">🔥 {approximateCalories} calories</span>
                )}
              </div>

              {badges.length > 0 && (
                <>
                  <div className="stats-divider">•</div>
                  <div className="dietary-icons-airbnb">
                    {badges.map((badge, index) => (
                      <span key={index} className="dietary-icon-hover" title={badge.label}>
                        {badge.icon}
                      </span>
                    ))}
                  </div>
                </>
              )}
            </div>

            {nutritionalInformation && (
              <div className="nutrition-summary-airbnb">
                <p>{nutritionalInformation}</p>
              </div>
            )}

            {/* Food Description Section */}
            {description && (
              <div className="description-section-airbnb">
                <h3 className="section-title-airbnb">Description of dish</h3>
                <p className="description-text-airbnb">{description}</p>
              </div>
            )}

            {/* Detailed Allergen Breakdown */}
            <div className="nutrient-breakdown-airbnb">
              <div className="breakdown-header">
                <h3 className="section-title-airbnb">Detailed Allergen Breakdown</h3>
                {!isPayingUser && (
                  <div className="premium-badge-modal">
                    <span className="premium-icon">💎</span>
                    <span>Premium</span>
                  </div>
                )}
                <span className="allergen-breakdown-note">
                  Always make sure to double check with restaurant before ordering. We can only give high/low
                  probability of allergen risk ⚠️.
                </span>
              </div>
              <div className={`nutrient-grid-airbnb ${!isPayingUser ? 'blurred-section' : ''}`}>
                {allergenBreakdown.map((allergen, index) => (
                  <div
                    key={index}
                    className={`nutrient-item-airbnb ${allergen.isBlurred ? 'locked nutrient-item-clickable' : ''}`}
                    onClick={allergen.isBlurred ? handleUpgradeClick : undefined}
                    onMouseEnter={allergen.isBlurred ? () => setShowNutrientTooltip(index) : undefined}
                    onMouseLeave={allergen.isBlurred ? () => setShowNutrientTooltip(null) : undefined}
                  >
                    <div className="allergen-item-content">
                      <span className="nutrient-name-airbnb">{allergen.key}</span>
                      <div className="nutrient-level-airbnb">
                        <span className="nutrient-icon-airbnb" style={{ color: allergen.color }}>
                          {allergen.icon}
                        </span>
                        <span className={`nutrient-text-airbnb ${allergen.isBlurred ? 'blurred' : ''}`}>
                          {allergen.displayValue}
                        </span>
                      </div>
                    </div>
                    {showNutrientTooltip === index && allergen.isBlurred && (
                      <div className="upgrade-tooltip-modal nutrient-tooltip">🔒 Upgrade to unlock</div>
                    )}
                  </div>
                ))}
              </div>
              {!isPayingUser && (
                <div className="allergen-upgrade-overlay" onClick={handleUpgradeClick}>
                  <div className="upgrade-message">
                    <span className="upgrade-icon">🔒</span>
                    <span>Upgrade to access detailed allergen information</span>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FoodDetailModal;
